import { RefObject, useCallback, useEffect } from 'react';
import { MapRef } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import useMapHelpers from './MapHelpers';
import { bbox } from '@turf/bbox';
import { DefaultMapEntity } from '../types';
import { setAreaLoaded } from '../../../redux/slices/mapSlice';

const useViewController = (mapRef?: RefObject<MapRef>) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const selectedCompound: DefaultMapEntity = useSelector(
    (state: any) => state.map.selectedCompound
  );
  const selectedArea: DefaultMapEntity = useSelector(
    (state: any) => state.map.selectedArea
  );
  const dispatch = useDispatch();
  const firstDestination = useSelector(
    (state: any) => state.map.firstDestination
  );
  const secondDestination = useSelector(
    (state: any) => state.map.secondDestination
  );
  const mapConfig = useSelector((state: any) => state.map.config);
  const { getBoundsPadding } = useMapHelpers();
  const compoundSelectZoomListener = useCallback(() => {
    if (mapRef?.current && selectedCompound) {
      const feature: any = {
        type: 'Feature',
        geometry: {
          type: selectedCompound?.geometry?.type,
          coordinates: selectedCompound?.geometry?.coordinates
        },
        properties: {
          id: selectedCompound.id,
          isSelected: true
        }
      };
      if (feature?.geometry?.coordinates?.length > 0) {
        const [minLng, minLat, maxLng, maxLat] = bbox(feature);
        {
          mapRef.current.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            {
              padding: isMobile ? 20 : 40,
              duration: 600,
              animate: true,
              essential: true
            }
          );
        }
      }
    }
  }, [selectedCompound, mapRef]);

  const areaSelectZoomListener = useCallback(() => {
    if (mapRef?.current && selectedArea?.geometry?.coordinates) {
      const feature: any = {
        type: 'Feature',
        geometry: {
          type: selectedArea?.geometry?.type,
          coordinates: selectedArea?.geometry?.coordinates
        },
        properties: {
          id: selectedArea.id,
          isSelected: true
        }
      };
      if (feature?.geometry?.coordinates?.length > 0) {
        const [minLng, minLat, maxLng, maxLat] = bbox(feature);

        mapRef.current
          .fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            {
              padding: 40,
              duration: 600,
              animate: true,
              essential: false,
              zoom: 10.5
            }
          )
          .on('idle', () => {
            dispatch(setAreaLoaded(true));
          });
      }
    }
  }, [selectedArea, mapRef]);

  useEffect(() => {
    if (mapRef?.current) {
      compoundSelectZoomListener();
    }
  }, [selectedCompound, mapRef?.current]);

  useEffect(() => {
    if (mapRef?.current && selectedCompound == null) {
      areaSelectZoomListener();
    }
  }, [selectedArea, mapRef?.current]);
  useEffect(() => {
    if (mapRef?.current && firstDestination && secondDestination) {
      const distance = Math.abs(
        firstDestination.northcoastKm - secondDestination.northcoastKm
      );
      mapRef.current.fitBounds(
        [firstDestination?.coordinates, secondDestination?.coordinates],
        {
          animate: true,
          essential: true,
          duration: 600,
          padding: getBoundsPadding(),
          retainPadding: false,
          ...((distance > 100 || distance == 0) && {
            zoom: mapConfig?.desktop.areasMaxZoom + 0.1
          })
        }
      );
    }
  }, [firstDestination, secondDestination]);
};

export default useViewController;
